import React, { useState } from "react";
import { Bell, Send, X, AlertTriangle, Save, ChevronDown, ChevronUp, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TabsContent } from "@/components/ui/tabs";
import { formatTime12h } from "../eventTime";

const AlertCard = ({ alert, templates, onSend, onDismiss }) => {
  const [subject, setSubject] = useState(alert.subject || "");
  const [body, setBody] = useState(alert.body || "");
  const [extra, setExtra] = useState((alert.extra_emails || []).join(", "));
  const [templateId, setTemplateId] = useState("");
  const crewCount = (alert.crew_emails || []).length;

  const fillPlaceholders = (text) => {
    const map = {
      event_name: alert.event_name || "",
      event_date: alert.event_date || "",
      crew_call_time: alert.call_time ? formatTime12h(alert.call_time) : "",
      show_time: alert.start_time ? formatTime12h(alert.start_time) : "",
      start_time: alert.start_time ? formatTime12h(alert.start_time) : "",
      end_time: alert.end_time ? formatTime12h(alert.end_time) : "",
      location: alert.location || "",
      change: alert.change_summary || "",
      change_summary: alert.change_summary || "",
    };
    return (text || "").replace(/\{(\w+)\}/g, (m, key) => (key in map ? map[key] : m));
  };

  const applyTemplate = (id) => {
    setTemplateId(id);
    const t = (templates || []).find((x) => x.id === id);
    if (t) {
      setSubject(fillPlaceholders(t.subject || ""));
      setBody(fillPlaceholders(t.body || ""));
    }
  };

  const send = () => {
    const extra_emails = extra.split(",").map((e) => e.trim()).filter((e) => e.includes("@"));
    onSend(alert.id, { subject, body, extra_emails });
  };

  return (
    <div className="bg-black/40 border border-zinc-800 rounded-sm p-4" data-testid={`alert-card-${alert.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2">
            {alert.cancelled && (
              <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded font-bold">CANCELLED</span>
            )}
            <h3 className="text-white font-bold">{alert.event_name}</h3>
          </div>
          <p className="text-zinc-400 text-sm mt-1">{alert.event_date || "TBD"} • {alert.change_summary}</p>
        </div>
        <span className="text-xs text-zinc-500 whitespace-nowrap">
          {crewCount > 0 ? `${crewCount} accepted crew + staff` : "staff only"}
        </span>
      </div>

      <div className="space-y-3">
        <div>
          <Label className="text-xs text-zinc-500 uppercase font-bold">Load a template (optional)</Label>
          <Select value={templateId} onValueChange={applyTemplate}>
            <SelectTrigger className="bg-black border-zinc-700 text-white mt-1" data-testid={`alert-template-${alert.id}`}>
              <SelectValue placeholder="Choose a saved template..." />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              {(templates || []).map((t) => (
                <SelectItem key={t.id} value={t.id} className="text-white hover:bg-zinc-800">{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-zinc-600 text-xs mt-1">
            Placeholders auto-fill from this event: {"{event_name}"} {"{event_date}"} {"{crew_call_time}"} {"{show_time}"} {"{end_time}"} {"{location}"} {"{change}"}
          </p>
        </div>
        <div>
          <Label className="text-xs text-zinc-500 uppercase font-bold">Subject</Label>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="bg-black border-zinc-700 text-white mt-1"
            data-testid={`alert-subject-${alert.id}`}
          />
        </div>
        <div>
          <Label className="text-xs text-zinc-500 uppercase font-bold">Message</Label>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            className="bg-black border-zinc-700 text-white mt-1"
            data-testid={`alert-body-${alert.id}`}
          />
        </div>
        <div>
          <Label className="text-xs text-zinc-500 uppercase font-bold">
            Extra recipients (staff, comma-separated) — {crewCount} accepted crew are added via BCC automatically
          </Label>
          <Input
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            className="bg-black border-zinc-700 text-white mt-1"
            data-testid={`alert-extra-${alert.id}`}
          />
        </div>
      </div>

      <div className="flex gap-2 mt-4">
        <Button
          onClick={send}
          className="bg-crew-yellow text-black font-bold uppercase hover:bg-white"
          data-testid={`alert-send-${alert.id}`}
        >
          <Send className="mr-2 h-4 w-4" /> Send Alert
        </Button>
        <Button
          onClick={() => onDismiss(alert.id)}
          variant="outline"
          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          data-testid={`alert-dismiss-${alert.id}`}
        >
          <X className="mr-2 h-4 w-4" /> Dismiss
        </Button>
      </div>
    </div>
  );
};

export const AlertsTab = (props) => {
  const { changeAlerts, alertRecipients, emailTemplates, handleSendAlert, handleDismissAlert, handleSaveAlertRecipients } = props;
  const [recipientsText, setRecipientsText] = useState((alertRecipients || []).join(", "));
  const [showHistory, setShowHistory] = useState(false);

  const pending = (changeAlerts || []).filter((a) => a.status === "pending");
  const sent = (changeAlerts || [])
    .filter((a) => a.status === "sent")
    .sort((a, b) => (b.sent_at || "").localeCompare(a.sent_at || ""));

  const saveRecipients = () => {
    const emails = recipientsText.split(",").map((e) => e.trim()).filter((e) => e.includes("@"));
    handleSaveAlertRecipients(emails);
  };

  return (
    <TabsContent value="alerts" data-testid="alerts-content">
      <div className="bg-crew-zinc border border-zinc-800 rounded-sm overflow-hidden">
        <div className="p-6 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <Bell className="w-6 h-6 text-crew-yellow" />
            <h2 className="text-2xl font-bold text-crew-yellow">CHANGE ALERTS</h2>
          </div>
          <p className="text-zinc-400 text-sm mt-1">
            When a Monday.com sync detects a cancellation or a date/time change, a ready-to-send crew alert is
            prepared here. Review it, then send — accepted crew are added via BCC along with the default staff list.
          </p>
        </div>

        {/* Default staff recipients editor */}
        <div className="p-4 border-b border-zinc-800 bg-zinc-900/40">
          <Label className="text-xs text-zinc-500 uppercase font-bold">Default staff recipients (always added via BCC)</Label>
          <div className="flex gap-2 mt-1">
            <Input
              value={recipientsText}
              onChange={(e) => setRecipientsText(e.target.value)}
              placeholder="staff1@example.org, staff2@example.org"
              className="bg-black border-zinc-700 text-white"
              data-testid="alert-default-recipients-input"
            />
            <Button
              onClick={saveRecipients}
              variant="outline"
              className="border-crew-yellow text-crew-yellow hover:bg-crew-yellow hover:text-black"
              data-testid="save-alert-recipients-btn"
            >
              <Save className="mr-2 h-4 w-4" /> Save
            </Button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {pending.length === 0 ? (
            <div className="p-12 text-center text-zinc-500" data-testid="no-alerts-message">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-40" />
              <p>No pending alerts. Everything is up to date.</p>
            </div>
          ) : (
            pending.map((alert) => (
              <AlertCard
                key={alert.id}
                alert={alert}
                templates={emailTemplates}
                onSend={handleSendAlert}
                onDismiss={handleDismissAlert}
              />
            ))
          )}
        </div>

        {/* Sent history */}
        <div className="p-4 border-t border-zinc-800">
          <button
            onClick={() => setShowHistory((v) => !v)}
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-crew-yellow transition-colors"
            data-testid="toggle-sent-history"
          >
            {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            View sent history ({sent.length})
          </button>
          {showHistory && (
            <div className="mt-3" data-testid="sent-history-list">
              {sent.length === 0 ? (
                <p className="text-zinc-500 text-sm">No alerts have been sent yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-zinc-500 border-b border-zinc-800">
                      <tr>
                        <th className="py-2 px-3 text-left">Event</th>
                        <th className="py-2 px-3 text-left">Event Date</th>
                        <th className="py-2 px-3 text-left">Change</th>
                        <th className="py-2 px-3 text-left">Sent</th>
                        <th className="py-2 px-3 text-center">#</th>
                        <th className="py-2 px-3 text-left">Sent To</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800/60">
                      {sent.map((a) => (
                        <tr key={a.id} data-testid={`sent-alert-${a.id}`}>
                          <td className="py-2 px-3 text-white">
                            {a.cancelled && (
                              <span className="text-[10px] bg-red-500 text-white px-1.5 py-0.5 rounded font-bold mr-2">CANCELLED</span>
                            )}
                            {a.event_name}
                          </td>
                          <td className="py-2 px-3 text-zinc-300 font-mono">{a.event_date || "TBD"}</td>
                          <td className="py-2 px-3 text-zinc-400">{a.change_summary}</td>
                          <td className="py-2 px-3 text-zinc-400">
                            {a.sent_at ? new Date(a.sent_at).toLocaleString() : "—"}
                          </td>
                          <td className="py-2 px-3 text-center">
                            <span className="inline-flex items-center gap-1 text-green-400 font-bold">
                              <CheckCircle className="w-3 h-3" /> {a.sent_to_count ?? 0}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-zinc-400 text-xs break-all max-w-md">
                            {(a.sent_recipients && a.sent_recipients.length
                              ? a.sent_recipients
                              : [...(a.crew_emails || []), ...(a.extra_emails || [])]
                            ).join(", ") || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </TabsContent>
  );
};
